import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { CompactType, GridsterConfig, GridsterItem, GridType } from 'angular-gridster2';
import { DisplayGrid } from 'angular-gridster2';
import { difference } from 'lodash-es';

@Component({
  selector: 'vb-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: false  //NOSONAR
})
export class LayoutComponent implements OnInit, OnChanges {
  @Input() initialData: any;
  @Input() reset: any;
  @Input() layoutOptions: any = {};
  @Output() updatedCSS = new EventEmitter<any>();
  options: GridsterConfig = {
    itemResizeCallback: this.itemResize.bind(this),
    gridType: GridType.Fit,
    compactType: CompactType.None,
    margin: 1,
    outerMargin: true,
    outerMarginTop: null,
    outerMarginRight: null,
    outerMarginBottom: null,
    outerMarginLeft: null,
    useTransformPositioning: true,
    mobileBreakpoint: 340,
    minCols: 12,
    maxCols: 12,
    minRows: 4,
    maxRows: 4,
    maxItemCols: 12,
    minItemCols: 1,
    maxItemRows: 4,
    minItemRows: 1,
    maxItemArea: 600,
    minItemArea: 1,
    defaultItemCols: 1,
    defaultItemRows: 1,
    fixedColWidth: 50,
    fixedRowHeight: 50,
    keepFixedHeightInMobile: false,
    keepFixedWidthInMobile: false,
    scrollSensitivity: 10,
    scrollSpeed: 20,
    enableEmptyCellClick: false,
    enableEmptyCellContextMenu: false,
    enableEmptyCellDrop: false,
    enableEmptyCellDrag: false,
    enableOccupiedCellDrop: false,
    emptyCellDragMaxCols: 12,
    emptyCellDragMaxRows: 4,
    ignoreMarginInRow: false,
    displayGrid: DisplayGrid.Always,
    draggable: {
      delayStart: 0,
      enabled: true,
      ignoreContentClass: 'gridster-item-content',
      ignoreContent: false,
      dragHandleClass: 'drag-handler',
      dropOverItems: false,
      stop: this.onDragStop.bind(this)
    },
    resizable: {
      enabled: true,
      stop: this.onResizeStop.bind(this)
    }
  };
  dashboard: GridsterItem[] = [];
  removedItems: GridsterItem[] = [];

  containerName = 'columns';
  containerItems = ['column1', 'column2'];
  gridsterMetaData = [];
  calculatedCSS: any = null;
  rawCSS: any = null;

  itemChange(item: any, itemComponent: any) {
    this.getCss();
  }

  saveCalculatedStyles() {
    this.updatedCSS.emit({
      css: this.calculatedCSS,
      raw: this.rawCSS
    });
  }

  itemResize(item: any, itemComponent: any) {
    this.getCss();
  }

  onItemRemove(gridsterItem: any, gridsterItemComponent: any) {
    const item = this.dashboard.find((d) => d.id === gridsterItem.id);
    if (item) {
      this.dashboard.splice(this.dashboard.indexOf(item), 1);
      this.removedItems.push(item);
      this.getCss();
    }
  }

  createInitialDashboard() {
    const _initData = this.initialData;
    // this.dashboard = [];

    if (_initData?.alignment?.raw) {
      const currentIds = _initData.items.map((e) => e.id);
      let rawCSS = this.rawCSS
        ? this.rawCSS.filter((e) => currentIds.includes(e.id))
        : _initData.alignment.raw.filter((e) => currentIds.includes(e.id));
      const configuredIds = this.rawCSS ? this.rawCSS.map((e) => e.id) : rawCSS.map((e) => e.id);

      const deletedFields = difference(configuredIds, currentIds);
      let updatecss = false;

      // Step 1: Unmark cells in `gridMatrix` for deleted items
      if (deletedFields.length > 0) {
        updatecss = true;
        deletedFields.forEach((id) => {
          const deletedItem = this.rawCSS.find((e) => e.id === id);
          if (deletedItem) {
            // Unmark cells for the deleted item
            this.unmarkCellsOccupied(this.gridMatrix, deletedItem.x, deletedItem.y, deletedItem.cols, deletedItem.rows);
          }
        });
        // Remove deleted items from rawCSS
        rawCSS = rawCSS.filter((e) => !deletedFields.includes(e.id));
      }

      // Step 2: Mark cells for existing items in `rawCSS`
      rawCSS.forEach((item) => {
        this.markCellsOccupied(this.gridMatrix, item.x, item.y, item.cols, item.rows);
      });

      // Step 3: Add only new fields to the next available cells
      const newFields = difference(currentIds, configuredIds);
      if (newFields.length > 0) {
        updatecss = true;
        const newItems: any = []; // Collect new items separately
        newFields.forEach((f) => {
          const newObj = _initData.items.find((e) => e.id === f);
          const cols = newObj.cols || 1;
          const rows = newObj.rows || 1;

          // Find next available cell for the new item
          const { x, y } = this.findNextAvailableCells(this.gridMatrix, cols, rows);

          // Add new item to the temporary array
          newItems.push({
            cols: cols,
            rows: rows,
            y: y,
            x: x,
            minItemRows: 1,
            minItemCols: 1,
            id: newObj.id,
            fkId: newObj.fkId,
            fkTableSid: newObj.fkTableSid,
            fkAppId: newObj.fkAppId,
            label: newObj.label
          });

          // Mark the new item's cells as occupied
          this.markCellsOccupied(this.gridMatrix, x, y, cols, rows);
        });

        // Update the dashboard with existing items and newly added items
        this.dashboard = [...rawCSS, ...newItems] as GridsterItem[];
      } else {
        // No new fields to add, just keep existing items
        this.dashboard = [...rawCSS] as GridsterItem[];
      }

      if (updatecss) {
        this.getCss();
        // this.saveCalculatedStyles();
      }
    } else {
      const items = this.initialData.items;
      const currentIds = items.map((e) => e.id);

      // Step 1: Remove outdated items from `dashboard` and update `gridMatrix`
      this.dashboard = this.dashboard.filter((d) => {
        if (!currentIds.includes(d.id)) {
          // Item is no longer in `items`, so remove it and free up the cells
          this.unmarkCellsOccupied(this.gridMatrix, d.x, d.y, d.cols, d.rows);
          return false; // Exclude from `dashboard`
        }
        return true; // Keep item in `dashboard`
      });

      // Step 2: Add new items that are not in the dashboard
      items.forEach((e: any) => {
        const exists = this.dashboard.some((d) => d.id === e.id);

        if (!exists) {
          // Only add the item if it does not already exist
          const { x, y } = this.findNextAvailableCells(this.gridMatrix, 1, 1);

          this.dashboard.push({
            cols: 1,
            rows: 1,
            y: y,
            x: x,
            minItemRows: 1,
            minItemCols: 1,
            id: e.id,
            label: e.label,
            fkId: e.fkId,
            fkTableSid: e.fkTableSid,
            fkAppId: e.fkAppId,
          });

          this.markCellsOccupied(this.gridMatrix, x, y, 1, 1);
        }
      });
    }
  }

  unmarkCellsOccupied(gridMatrix: any[][], x: number, y: number, cols: number, rows: number) {
    for (let i = y; i < y + rows; i++) {
      for (let j = x; j < x + cols; j++) {
        if (gridMatrix[i] && gridMatrix[i][j]) {
          gridMatrix[i][j] = false; // Mark cell as unoccupied
        }
      }
    }
  }

  findNextAvailableCells(gridMatrix: any[][], cols: number, rows: number) {
    // First pass: Search for gaps in the previous rows
    for (let y = 0; y < gridMatrix.length; y++) {
      for (let x = 0; x < gridMatrix[y].length; x++) {
        if (this.canPlaceItem(gridMatrix, x, y, cols, rows)) {
          // Found an available cell in a previous row
          return { x, y };
        }
      }
    }

    // If no gaps were found, expand the grid and find the next available cell
    return this.expandGridAndFind(cols, rows);
  }

  canPlaceItem(gridMatrix: any[][], x: number, y: number, cols: number, rows: number): boolean {
    for (let i = y; i < y + rows; i++) {
      for (let j = x; j < x + cols; j++) {
        // Ensure bounds checking in case the grid isn't large enough
        if (!gridMatrix[i] || gridMatrix[i][j]) {
          return false; // Cell is occupied or out of bounds
        }
      }
    }
    return true; // Cells are available
  }

  expandGridAndFind(cols: number, rows: number) {
    // Return the next available cell
    return { x: 0, y: 0 };
  }

  markCellsOccupied(gridMatrix: any[][], x: number, y: number, cols: number, rows: number) {
    for (let i = y; i < y + rows; i++) {
      for (let j = x; j < x + cols; j++) {
        if (!gridMatrix[i]) {
          gridMatrix[i] = []; // Initialize row if it doesn't exist
        }
        gridMatrix[i][j] = true; // Mark the cell as occupied
      }
    }
  }

  // Update CSS and dashboard on drag or resize stop
  updateAndGetCss(item: any, gridsterItem: any) {
    const index = this.dashboard.findIndex((d) => d.id === item.id);
    this.dashboard[index] = { ...item, ...gridsterItem.$item };
    this.getCss();
  }

  // Update CSS based on current dashboard state
  getCss() {
    const metaData: GridsterItem[] = this.dashboard;
    const maxColumns = Math.max(...metaData.map((d) => d.x + d.cols));
    const maxRows = Math.max(...metaData.map((d) => d.y + d.rows));

    let styles = `.container { display:grid; grid-template-columns: repeat(${maxColumns}, 1fr); grid-template-rows: repeat(${maxRows}, 1fr); grid-gap: 0px; }`;
    metaData.forEach((item) => {
      styles += `#${item.id} { grid-row: ${item.y + 1} / ${item.y + 1 + item.rows}; grid-column: ${item.x + 1} / ${item.x + 1 + item.cols}; }`;
    });

    this.calculatedCSS = styles;
    this.rawCSS = metaData;
  }

  onDragStop(item: any, gridsterItem: any) {
    this.unmarkCellsOccupied(this.gridMatrix, item.x, item.y, item.cols, item.rows);
    const { x, y } = gridsterItem.$item;
    const updatedItem = { ...item, x, y };
    this.markCellsOccupied(this.gridMatrix, x, y, updatedItem.cols, updatedItem.rows);
    this.updateAndGetCss(updatedItem, gridsterItem);
  }

  onResizeStop(item: any, gridsterItem: any) {
    this.unmarkCellsOccupied(this.gridMatrix, item.x, item.y, item.cols, item.rows);
    const { cols, rows } = gridsterItem.$item;
    const updatedItem = { ...item, cols, rows };
    this.markCellsOccupied(this.gridMatrix, item.x, item.y, cols, rows);
    this.updateAndGetCss(updatedItem, gridsterItem);
  }

  fillMatrix(items) {
    const columns = 4;
    const matrix: any = [];

    // Calculate the number of rows needed
    const rowsNeeded = Math.ceil(items.length / columns);

    for (let row = 0; row < rowsNeeded; row++) {
      matrix[row] = [];
      for (let col = 0; col < columns; col++) {
        const index = row * columns + col;
        if (index < items.length) {
          matrix[row][col] = items[index];
        } else {
          matrix[row][col] = null; // Or any placeholder for empty cells
        }
      }
    }

    return matrix;
  }
  gridMatrix: boolean[][]; // Define gridMatrix as a class-level property
  gridSize = 4; // Define the grid size, for example, a 4x4 grid

  ngOnInit() {
    this.initializeGridMatrix(); // Initialize gridMatrix
    this.options = { ...this.options, ...this.layoutOptions };
    this.createInitialDashboard();
  }

  initializeGridMatrix() {
    // Only initialize if it's not already defined
    if (!this.gridMatrix) {
      this.gridMatrix = Array.from({ length: this.gridSize }, () => Array(this.gridSize).fill(false));
    }
  }

  ngOnChanges(changes: any) {
    const dataChanged =
      changes.initialData &&
      changes.initialData.previousValue &&
      JSON.stringify(changes.initialData.previousValue) !== JSON.stringify(changes.initialData.currentValue);
    if (dataChanged) {
      this.options = { ...this.options, ...this.layoutOptions };
      this.createInitialDashboard();
    }
  }

  deleteItem(deletedItem: GridsterItem, $event: MouseEvent | TouchEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    const item = this.dashboard.find((d) => d.id === deletedItem.id);
    if (item) {
      this.dashboard.splice(this.dashboard.indexOf(item), 1);
      this.removedItems.push(item);
      this.getCss();
    }
  }

  addItem(deletedItem: GridsterItem): void {
    const item = this.removedItems.find((d) => d.id === deletedItem.id);
    if (deletedItem) {
      this.dashboard.push(deletedItem);
      this.removedItems.splice(this.removedItems.indexOf(deletedItem), 1);
      this.getCss();
    }
  }
}
